import { Injectable } from '@angular/core';

import { MultipleColumnItemDataInner, ParagraphMultipleColumnData, ParagraphMultipleColumnItemData } from '@generated/content';
import { CmsComponentConfig } from '@interfaces/config';
import { CmsComponentNodeData } from '@interfaces/cms-component-node-data';
import { MultipleColumn } from '@interfaces/config/cms-components/multiple-column-config';

import { BaseComponentResolver } from '@services/_base/base-component-resolver/base-component-resolver';
import { TMediaNodeType } from '@interfaces/media';
import { HeroSlider, LinkObject } from '@models/interfaces';
import { indexOf } from 'lodash-es';

@Injectable({ providedIn: 'root' })
export class MultipleColumnComponentResolver extends BaseComponentResolver {
  static override instance: MultipleColumnComponentResolver;
  private readonly componentType = MultipleColumn.TypeEnum;

  static getInstance(): MultipleColumnComponentResolver {
    return (MultipleColumnComponentResolver.instance ||= new MultipleColumnComponentResolver());
  }

  public override resolveComponentConfig(
    node: ParagraphMultipleColumnData,
    nodePageData: CmsComponentNodeData[]
  ): CmsComponentConfig<MultipleColumn.ComponentConfig> | null {
    if (!node) {
      return null;
    }

    this.nodePageData = nodePageData;

    return {
      componentType: this.componentType,
      config: {
        nodeId: BaseComponentResolver.getNodeId(node),
        columns: this.getColumns(node),
        field_alignment: this.getFieldAlignment(node),
        field_animation: this.getFieldAnimation(node),
        anchor_id: node.attributes.field_anchor_id,
      },
    };
  }

  private getFieldAlignment(node: ParagraphMultipleColumnData): string {
    if (node.attributes?.field_alignment) {
      return node.attributes?.field_alignment;
    }
    return null;
  }
  private getFieldAnimation(node: ParagraphMultipleColumnData): boolean {
    if (node.attributes?.field_animation) {
      return node.attributes?.field_animation;
    }
    return false;
  }

  private getColumns(node: ParagraphMultipleColumnData): MultipleColumn.ColumnConfig[] {
    if (!node.relationships.field_multiple_column_item?.data?.length) {
      return [];
    }

    return node.relationships.field_multiple_column_item.data.map(item => this.buildColumnConfig(item));
  }

  private getColumnImage(node: ParagraphMultipleColumnItemData): { url: string; scale: number } | null {
    if (!node.relationships?.field_image_multiplecolumn?.data) {
      return null;
    }

    const { id, type } = node.relationships.field_image_multiplecolumn.data;

    const mediaUrls = this.getMediaDataUrls(id, type as TMediaNodeType) || {};

    const url =
      mediaUrls.dataUrls['default'] ||
      mediaUrls.dataUrls['webp'] ||
      mediaUrls.dataUrls['medium'] ||
      mediaUrls.dataUrls['media_library'] ||
      mediaUrls.dataUrls['svg'] ||
      null;
    const parsedScale = parseInt(node.attributes.field_image_size_tmi, 10);
    const scale = isNaN(parsedScale) ? 1 : parsedScale * 0.01;

    return { url, scale };
  }

  private buildColumnConfig(node: MultipleColumnItemDataInner): MultipleColumn.ColumnConfig | null {
    const { id, type } = node;

    if (!(id && node)) {
      return null;
    }

    const columnNode = this.getNode<ParagraphMultipleColumnItemData>(id, type);

    if (!columnNode) {
      return null;
    }

    const image = this.getColumnImage(columnNode);
    let hoverText: any = '';
    if (
      columnNode.attributes.field_hover_text &&
      'processed' in columnNode.attributes.field_hover_text &&
      columnNode.attributes.field_hover_text.processed != null
    ) {
      hoverText = columnNode.attributes.field_hover_text.processed;
    }
    let animationValue;
    let animationType = columnNode.attributes.field_animation_type;

    if ('field_progressive_bar_value' in columnNode.attributes)
      animationValue = columnNode.attributes.field_progressive_bar_value;
    else animationValue = 50.0;
    let title: string = '0';
    let subtitle: string = '';
    let counter: number = 0;
    let showDecimals: boolean = false;
    if (animationType) {
      var match = columnNode.attributes.field_title_multiplecolumn.match(/^([\d.]+)(\D+)$/) || columnNode.attributes.field_title_multiplecolumn.match(/^(\D+)([\d.]+)$/);
      if (match) {
        if (match[1] === '$') {
          title = match[2];
          subtitle = match[1];
        } else {
          title = match[1]; // Extracts 2 as a number
          if (match.length > 2) subtitle = match[2]; // Extracts "%" as a string
        }
      } else {
        if (columnNode.attributes.field_title_multiplecolumn?.trim().match(/\d+/))
          title = columnNode.attributes.field_title_multiplecolumn?.trim().match(/\d+/)[0];
        if (columnNode.attributes.field_title_multiplecolumn?.trim().match(/\D+/)) {
          subtitle = columnNode.attributes.field_title_multiplecolumn?.trim().match(/\D+/)[0];
        }
      }

      counter = parseFloat(title);
      if (!Number.isInteger(counter)) {
        showDecimals = true;
      }
    }

    return {
      image,
      title: this.hyphenate(columnNode.attributes.field_title_multiplecolumn?.trim()),
      subtitle,
      description: this.hyphenate(columnNode.attributes.field_description_multiplecolumn?.trim()),
      link: MultipleColumnComponentResolver.extractLink(columnNode),
      bgColor: this.getBgColor(columnNode.attributes),
      hoverText: this.hyphenate(hoverText.trim()),
      hoverEffect: columnNode.attributes.field_hover_effect,
      animationType,
      animationValue,
      counter,
      showDecimals,
    };
  }

  private static extractLink(node: ParagraphMultipleColumnItemData): LinkObject | null {
    if (!node?.attributes?.field_link_multiplecolumn) {
      return null;
    }

    const { title, url, uri } = node?.attributes?.field_link_multiplecolumn;
    const href = url || uri;
    const external = href.startsWith('http');
    if (title && href) {
      return { label: this.hyphenate(title), href, external };
    }

    return null;
  }
}
